<template>
  <div id="app" class="container">
    <div v-if="cardView" class="card">
      <div class="columns is-mobile is-marginless is-paddingless">
        <div class="column is-full is-marginless is-paddingless">
          <b-navbar type="is-white" shadow transparent>
            <template slot="brand">
              <b-navbar-item tag="router-link" :to="{ path: '/' }">
                <img src="./assets/images/logo-pneudart-blue.svg" alt="Pneu-Dart">
              </b-navbar-item>
            </template>
            <template slot="start">
              <b-navbar-item tag="router-link" :to="{ path: '/' }">
                <i class="sl-house-2"></i> Home
              </b-navbar-item>
              <b-navbar-item tag="router-link" :to="{ path: '/product-requests' }">
                <i class="sl-box-2"></i> Product Requests
              </b-navbar-item>
              <b-navbar-item tag="router-link" :to="{ path: '/account' }">
                <i class="sl-profile-1"></i> Account
              </b-navbar-item>
              <b-navbar-item @click="toggleSupportRequestModal(true)">
                <i class="sl-buoy"></i> Support
              </b-navbar-item>
              <b-navbar-item tag="router-link" :to="{ path: '/product-request' }">
                <b-button type="is-primary">Create Product Request</b-button>
              </b-navbar-item>
            </template>
            <template slot="end">
              <b-navbar-item tag="div">
                <b-dropdown position="is-bottom-left" aria-role="menu">
                  <a class="navbar-item" slot="trigger" role="button">
                      <b>{{ fullName }} <span v-if="hasCustomer" class="has-text-grey-light">({{ customer.account_name }})</span></b>
                      <i class="sl-arrow-65 sl-sm sl-right"></i>
                  </a>
                  <b-dropdown-item has-link>
                    <router-link :to="{ path: '/account' }"><i class="sl-profile-1"></i> Account</router-link>
                  </b-dropdown-item>
                  <b-dropdown-item @click="toggleSupportRequestModal(true)">
                    <i class="sl-buoy"></i> Contact Support
                  </b-dropdown-item>
                  <b-dropdown-item has-link>
                    <router-link :to="{ path: '/login?logout' }"><i class="sl-log-out-1"></i> Logout</router-link>
                  </b-dropdown-item>
                </b-dropdown>
              </b-navbar-item>
            </template>
          </b-navbar>
        </div>
      </div>
      <div class="columns is-mobile is-marginless is-paddingless is-vertical-center has-background-white">
        <router-view></router-view>
      </div>
    </div>
    <div v-else>
      <div class="columns is-mobile is-marginless is-paddingless is-vertical-center">
        <router-view></router-view>
      </div>
    </div>
    <footer class="footer">
      <div class="container">
        <div class="content has-text-centered">
          <p class="has-text-grey-light">&copy; {{ year }} Pneu-Dart Inc. All Rights Reserved. </p>
          <p v-if="isDevelopment" class="has-text-grey-light is-size-7">Environment: {{ nodeEnvironment }}</p>
          <p v-if="isDevelopment" class="has-text-left"><pre class="has-text-grey-light is-size-7">{{ environmentVariables }}</pre></p>
        </div>
      </div>
    </footer>
    <b-modal :active.sync="isSupportRequestModalActive" class="support-request-modal" has-modal-card trap-focus>
      <support-request-modal></support-request-modal>
    </b-modal>
  </div>
</template>

<script>
import store from '@/store'
import { mapGetters, mapActions } from 'vuex'
import { titleCase } from '@/filters'
import moment from 'moment'

import SessionExpiringModal from '@/components/SessionExpiringModal'
import SupportRequestModal from '@/components/SupportRequestModal'

export default {
  data: function () {
      return {
        nowInterval: null,
        now: moment(),
        sessionExpiringModal: null,
        isSupportRequestModalActive: false
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'customer',
      'token',
      'expires'
    ]),
    isDevelopment () {
      return process.env.NODE_ENV !== 'production'
    },
    nodeEnvironment () {
      return process.env.NODE_ENV
    },
    environmentVariables () {
      return JSON.stringify(process.env, null, 2)
    },
    hasCustomer () {
      return (this.user.customer_id) ? true : false
    },
    fullName () {
      return `${ this.user.first_name } ${ this.user.last_name }`
    },
    year () {
      return moment().format('Y')
    },
    cardView () {
      return ! ['login','reset'].includes(this.$route.name)
    },
    expiration () {
      return (this.expires) ? moment(this.expires) : null
    },
    sessionLessThanFiveMinutesBeforeExpiration () {
      return this.token && this.expires && this.now.isAfter(moment(this.expiration.format()).subtract(5, 'm'))
    },
    sessionLessThanOneMinuteBeforeExpiration () {
      return this.token && this.expires && this.now.isAfter(moment(this.expiration.format()).subtract(1, 'm'))
    },
    sessionExpired () {
      return ! this.token || ! this.expires || this.now.isAfter(this.expiration)
    }
  },
  watch: {
    now: function () { // Buefy's modal props aren't reactive, so we watch for changes and force an update on the modal
      if (this.sessionExpiringModal) {
        this.sessionExpiringModal.props.now = this.now
        this.sessionExpiringModal.$forceUpdate()
      }
    },
    expiration: function () { // Buefy's modal props aren't reactive, so we watch for changes and force an update on the modal
      if (this.sessionExpiringModal) {
        this.sessionExpiringModal.props.expiration = this.expiration
        this.sessionExpiringModal.$forceUpdate() // Buefy's modal props aren't reactive, so we watch for changes and force an update on the modal
      }
    },
    sessionExpired: function (newSessionExpired, previousSessionExpired) { // If session expired: deauthenticate, close modal, clear modal property, and redirect to login page
      if (newSessionExpired) { 
        this.deauthenticate()
        if (this.sessionExpiringModal) {
          this.sessionExpiringModal.close()
          this.sessionExpiringModal = null
        }
        if (this.$route.name !== 'login') {
          this.$router.push({
            path: '/login',
            // query: {
            //   r: window.btoa(`${ window.location.pathname }${ window.location.search }`)
            // }
          })
        }
      }
    },
    sessionLessThanFiveMinutesBeforeExpiration: function (newSessionLessThanFiveMinutesBeforeExpiration, previousSessionLessThanFiveMinutesBeforeExpiration) { // Within 5 minutes of expiration and still active, refresh auth silently; Move to interval if having issues with triggering consistency
      if (newSessionLessThanFiveMinutesBeforeExpiration && ! this.isAppIdle) {
        this.refreshAuthentication()
      }
    },
    sessionLessThanOneMinuteBeforeExpiration: function (newSessionLessThanOneMinuteBeforeExpiration, previousSessionLessThanOneMinuteBeforeExpiration) { // Within 1 minute of expiration, show "session expiring" modal; Move to interval if having issues with triggering consistency
      if (newSessionLessThanOneMinuteBeforeExpiration && ! this.sessionExpiringModal) {
        this.sessionExpiringModal = this.$buefy.modal.open({
          parent: this,
          component: SessionExpiringModal,
          props: {
            now: this.now,
            expiration: this.expiration
          },
          hasModalCard: true,
          trapFocus: true,
          canCancel: false
        })
      } else {
        this.sessionExpiringModal = null
      }
    }
  },
  methods: {
    ...mapActions([
      'deauthenticate',
      'refreshAuthentication'
    ]),
    toggleSupportRequestModal (display) {
      this.isSupportRequestModalActive = display
    },
  },
  components: {
    SessionExpiringModal,
    SupportRequestModal
  },
  filters: {
    titleCase
  },
  created () {
    this.nowInterval = setInterval(function () { // Update 'now' property every second (computed properties can't manage this properly)
      this.now = moment()
    }.bind(this), 1000)
  },
  destroyed () {
    clearInterval(this.nowInterval)
  }
}
</script>

<style lang="scss">
@import './styles/app'
</style>